import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { MatTabGroup } from '@angular/material/tabs';
import { Observable, zip } from 'rxjs';
import { SubSink } from 'subsink2';
import { Store } from '@ngrx/store';
import { LinkSubjectToTeamService, Pagination } from 'blg-akaun-ts-lib';
import { map } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AkaunMessageDialogComponent } from '../../../../../../../dialogues/akaun-message-dialog/akaun-message-dialog';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../visa';
import { MemberModel, UserTeamModel } from '../../../../../../../models/team-permissions.model';
import { TeamPermStates } from '../../../../../../../modules/permission/team-permissions-controller/states';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { TeamPermSelectors } from '../../../../../../../modules/permission/team-permissions-controller/selectors';
import { TeamPermActions } from '../../../../../../../modules/permission/team-permissions-controller/actions';
import { PermissionStates } from '../../../../../permission-controller/root';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
}

@Component({
  selector: 'app-team-members-edit',
  templateUrl: './team-members-edit.component.html',
  styleUrls: ['./team-members-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class TeamMembersEditComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 3;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  // info. about previous local states
  prevIndex: number;
  private prevLocalState: any;

  // api visa
  apiVisa = AppConfig.apiVisa;

  toggleColumn$: Observable<boolean>;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  form: FormGroup;
  formDisabled: boolean = false;
  // fields that require options
  // rank
  rank: string;
  rankArr: any = ['OWNER', 'MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
  newRankArr: any = [];

  currentUser: UserTeamModel;
  selectedMember: MemberModel;
  teamGuid: string;

  akaunMessageDialogComponentMatDialogRef: MatDialogRef<AkaunMessageDialogComponent>;

  constructor(
    private linkSubjectToTeamService: LinkSubjectToTeamService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private readonly componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      currRank: [''],
      rank: ['', Validators.compose([Validators.required])]
    })

    this.newRankArr = this.rankArr;

    const currentUser$ = this.store.select(TeamPermSelectors.selectCurrentUser);
    const selectedMember$ = this.store.select(TeamPermSelectors.selectMember);
    const selectedTeamGuid$ = this.store.select(TeamPermSelectors.selectGuid);

    // get the current user and current selected team guid
    this.subSink.sink = zip(currentUser$, selectedTeamGuid$).subscribe(
      ([user, teamGuid]) => {
      this.currentUser = user;
      this.teamGuid = teamGuid;
    })

    // get the currently selected member and set/disable form accordingly
    this.subSink.sink = selectedMember$.subscribe(member => {
      this.selectedMember = member;
      this.setForm();
      this.disableForm();
    })
  }

  /**
   * Set the details of the form.
   */
   setForm() {
    this.form.patchValue({
      rank: this.selectedMember.link_subject_to_grp.rank
    })
  }

  /**
   * Disable the form based on the current user's rank.
   */
   disableForm() {
    const isInTeam = this.currentUser.link_subject_to_grps.map(x => x.app_grp_guid.toString()).includes(this.teamGuid);
    const currentUserRank = isInTeam ? this.currentUser.link_subject_to_grps.filter(x => x.app_grp_guid.toString() == this.teamGuid)[0].rank : null;
    const selectedUserRank = this.selectedMember.link_subject_to_grp.rank;

    if (currentUserRank == 'GUEST' || currentUserRank == 'VISITOR' || currentUserRank == 'ANONYMOUS' || !currentUserRank) {
      this.formDisabled = true;
      this.resetRankArray()
    } else if (currentUserRank == 'MEMBER' && (selectedUserRank == 'OWNER' || selectedUserRank == 'ADMIN')) {
      this.formDisabled = true;
      this.resetRankArray();
    } else if (currentUserRank == 'ADMIN' && selectedUserRank == 'OWNER') {
      this.formDisabled = true;
      this.resetRankArray();
    } else {
      this.formDisabled = false;
      this.setRankArray(currentUserRank);
    }
  }

  /**
   * Reset rank array.
   */
  resetRankArray() {
    this.rankArr = ['OWNER', 'MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    this.newRankArr = this.rankArr;
  }

  /**
   * Set the rank array based on the rank of the current user.
   *
   * @param currentUserRank : the rank of the current user
   */
   setRankArray(currentUserRank) {
    if (currentUserRank == 'OWNER') {
      this.rankArr = ['OWNER', 'MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'MANAGER') {
      this.rankArr = ['MANAGER', 'ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'ADMIN') {
      this.rankArr = ['ADMIN', 'MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'MEMBER') {
      this.rankArr = ['MEMBER', 'GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'GUEST') {
      this.rankArr = ['GUEST', 'VISITOR', 'ANONYMOUS'];
    } else if (currentUserRank == 'VISITOR') {
      this.rankArr = ['VISITOR', 'ANONYMOUS'];
    } else {
      this.rankArr = [];
    }
    this.newRankArr = this.rankArr;
  }

  /**
   * This function is called whe the user clicks on the save button.
   * This will dispatch an NgRx action to update details.
   *
   */
   onSubmit() {
     if (this.selectedMember.link_subject_to_grp.rank == 'OWNER') {
      // check how many owners left are there
      this.subSink.sink = this.getNumOwners().subscribe(x => {
        if (x > 1) {
        this.store.dispatch(TeamPermActions.updateMemberInit({guid: this.selectedMember.link_subject_to_grp.guid.toString(), member: this.form}))
        } else {
        this.akaunMessageDialogComponentMatDialogRef = this.dialog.open(AkaunMessageDialogComponent, {width: '400px'});
        this.akaunMessageDialogComponentMatDialogRef.componentInstance.confirmMessage = 'Cannot update the last remaining owner!';
        }
      })
    } else {
      this.store.dispatch(TeamPermActions.updateMemberInit({guid: this.selectedMember.link_subject_to_grp.guid.toString(), member: this.form}));
    }
  }

  /**
   * This function is called whe the user clicks on the delete button.
   * This will dispatch an NgRx action to delete the entity.
   *
   */
   onDelete() {
    if (this.selectedMember.link_subject_to_grp.rank == 'OWNER') {
      // check how many owners left are there
      this.subSink.sink = this.getNumOwners().subscribe(x => {
        if (x > 1) {
          this.store.dispatch(TeamPermActions.deleteMemberInit({guid: this.selectedMember.link_subject_to_grp.guid.toString()}));
          this.onReturn();
        } else {
        this.akaunMessageDialogComponentMatDialogRef = this.dialog.open(AkaunMessageDialogComponent, {width: '400px'});
        this.akaunMessageDialogComponentMatDialogRef.componentInstance.confirmMessage = 'Cannot delete the last remaining owner!';
        }
      })
    } else {
      this.store.dispatch(TeamPermActions.deleteMemberInit({guid: this.selectedMember.link_subject_to_grp.guid.toString()}));
      this.onReturn();
    }
  }

  /**
   * Returns an observable containing the number of owners in this team.
   *
   * @returns an observable containing the number of owners in this team
   */
  getNumOwners() {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'ranks', operator: '=', value: 'OWNER'}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
    .pipe(
      map(x => x.data.length)
    )
  }

  /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
   applyRankFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newRankArr = this.rankArr.filter( (option) =>
    option.toLowerCase().includes(filterValue) );
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    if (this.matTab) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        selectedIndex: this.matTab.selectedIndex
      });
    }
    this.subSink.unsubscribe();
  }

}
